/**
 * Batched query helper for Supabase .in() calls.
 *
 * PostgREST encodes .in() values in the URL query string. At ~1,000 UUIDs
 * (36 chars each), the URL can exceed browser/proxy limits (~8KB).
 * This helper chunks ID arrays and merges results to avoid silent truncation.
 *
 * Default chunk size: 200 UUIDs (~7.2KB per chunk with commas/parens).
 */

const DEFAULT_CHUNK_SIZE = 200;

/**
 * Splits an array into chunks of the given size.
 */
export function chunkArray<T>(arr: T[], size: number = DEFAULT_CHUNK_SIZE): T[][] {
  if (arr.length === 0) return [];
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * Deduplicates an array of strings (preserving first occurrence order).
 */
export function dedupeIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

/**
 * Executes a query function in batches over a large ID list.
 * Merges all results into a single flat array.
 *
 * @param ids - Full list of IDs (will be deduped and chunked)
 * @param queryFn - Async function that takes a chunk of IDs and returns results
 * @param chunkSize - Max IDs per batch (default 200)
 */
export async function batchedQuery<T>(
  ids: string[],
  queryFn: (chunk: string[]) => Promise<T[]>,
  chunkSize: number = DEFAULT_CHUNK_SIZE,
): Promise<T[]> {
  const unique = dedupeIds(ids);
  if (unique.length === 0) return [];

  const chunks = chunkArray(unique, chunkSize);
  const results = await Promise.all(chunks.map((chunk) => queryFn(chunk)));
  return results.flat();
}
